import { useEffect, useState, useCallback, useRef } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Save, Loader2, Upload, Image as ImageIcon, Video } from 'lucide-react';
import { toast } from 'sonner';
import BouncingLogoLoader from '@/components/BouncingLogoLoader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_HOST = process.env.REACT_APP_BACKEND_URL;

export default function HomepageSettings() {
  const { getAccessTokenSilently } = useAdmin();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const r = await fetch(`${API}/admin/homepage-settings`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) setSettings((await r.json()).settings);
    setLoading(false);
  }, [getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  const ss = settings?.submit_section || {};
  const updateSS = (k, v) => setSettings(s => ({ ...s, submit_section: { ...(s?.submit_section || {}), [k]: v } }));
  const ad = settings?.article_display || { show_inline_source_credit: true };
  const updateAD = (k, v) => setSettings(s => ({ ...s, article_display: { ...(s?.article_display || { show_inline_source_credit: true }), [k]: v } }));
  const pre = settings?.preloader || { animation: 'bounce' };
  const updatePre = (k, v) => setSettings(s => ({ ...s, preloader: { ...(s?.preloader || { animation: 'mina' }), [k]: v } }));
  const pf = settings?.personalized_feed || { enabled: true, title_hr: 'Za tebe', limit: 6 };
  const updatePF = (k, v) => setSettings(s => ({ ...s, personalized_feed: { ...(s?.personalized_feed || { enabled: true, title_hr: 'Za tebe', limit: 6 }), [k]: v } }));

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await getAccessTokenSilently();
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/admin/media/upload`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (r.ok) {
        const { url } = await r.json();
        updateSS('background_url', url);
        const type = file.type === 'video/mp4' ? 'video' : (file.type === 'image/gif' ? 'gif' : 'image');
        updateSS('background_type', type);
        toast.success(`Upload uspješan (${type})`);
      } else toast.error('Upload neuspješan');
    } catch { toast.error('Upload neuspješan'); }
    finally { setUploading(false); e.target.value = ''; }
  }

  async function save() {
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/admin/homepage-settings`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (r.ok) {
        toast.success('Spremljeno');
        // Invalidate sessionStorage so usePreloaderVariant picks up the new value
        try { sessionStorage.removeItem('emina_preloader_variant'); } catch {}
        load();
      } else toast.error('Greška');
    } finally { setSaving(false); }
  }

  if (loading || !settings) return <div className="p-10 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;

  const bgPreviewUrl = ss.background_url?.startsWith('/api/') ? `${BACKEND_HOST}${ss.background_url}` : ss.background_url;

  return (
    <div className="p-6 max-w-4xl" data-testid="homepage-settings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Postavke naslovnice</h1>
        <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded disabled:opacity-50" data-testid="homepage-save-btn">
          <Save size={14} /> {saving ? 'Spremam...' : 'Spremi postavke'}
        </button>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
        <h2 className="text-sm font-bold uppercase tracking-wider text-white">Sekcija "Predaj svoju vijest"</h2>

        <div>
          <label className="block text-xs text-zinc-400 mb-1">Vrsta pozadine</label>
          <div className="flex gap-2">
            {[{ k: 'image', label: 'Slika', Icon: ImageIcon }, { k: 'gif', label: 'GIF', Icon: ImageIcon }, { k: 'video', label: 'Video', Icon: Video }].map(({ k, label, Icon }) => (
              <button key={k} onClick={() => updateSS('background_type', k)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs rounded border ${ss.background_type === k ? 'bg-red-600 border-red-500 text-white' : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'}`}
                      data-testid={`bg-type-${k}`}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs text-zinc-400 mb-1">URL pozadine</label>
          <div className="flex gap-2">
            <input value={ss.background_url || ''} onChange={e => updateSS('background_url', e.target.value)}
                   placeholder="/api/uploads/... or https://..."
                   className="flex-1 px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-white text-sm" data-testid="bg-url-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs bg-zinc-700 hover:bg-zinc-600 text-white rounded disabled:opacity-50" data-testid="bg-upload-btn">
              <Upload size={12} /> {uploading ? 'Upload...' : 'Upload'}
            </button>
            <input ref={fileRef} type="file" accept="image/*,video/mp4" onChange={handleUpload} className="hidden" />
          </div>
          {bgPreviewUrl && (
            <div className="mt-3 rounded-lg overflow-hidden border border-zinc-800 max-w-md">
              {ss.background_type === 'video' ? (
                <video src={bgPreviewUrl} autoPlay muted loop className="w-full h-40 object-cover" data-testid="bg-preview-video" />
              ) : (
                <img src={bgPreviewUrl} alt="" className="w-full h-40 object-cover" data-testid="bg-preview-image" />
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs text-zinc-400 mb-1">Prozirnost overlaya: {(ss.overlay_opacity ?? 0.65).toFixed(2)}</label>
          <input type="range" min="0" max="1" step="0.05" value={ss.overlay_opacity ?? 0.65}
                 onChange={e => updateSS('overlay_opacity', parseFloat(e.target.value))}
                 className="w-full accent-red-600" data-testid="overlay-opacity-slider" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Naslov (HR)</label>
            <input value={ss.headline_hr || ''} onChange={e => updateSS('headline_hr', e.target.value)}
                   className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-white text-sm" data-testid="headline-input" />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Tekst gumba (HR)</label>
            <input value={ss.cta_label_hr || ''} onChange={e => updateSS('cta_label_hr', e.target.value)}
                   className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-white text-sm" data-testid="cta-label-input" />
          </div>
        </div>

        <div>
          <label className="block text-xs text-zinc-400 mb-1">Podnaslov (HR)</label>
          <textarea rows={2} value={ss.subheadline_hr || ''} onChange={e => updateSS('subheadline_hr', e.target.value)}
                 className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-white text-sm" data-testid="subheadline-input" />
        </div>

        <div>
          <label className="block text-xs text-zinc-400 mb-1">Link gumba</label>
          <input value={ss.cta_url || ''} onChange={e => updateSS('cta_url', e.target.value)}
                 placeholder="/predaj-vijest"
                 className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-white text-sm" data-testid="cta-url-input" />
        </div>

        <p className="text-[11px] text-zinc-500 mt-2">Na spremanje se naslov/podnaslov/gumb automatski prevode na 10 dodatnih jezika.</p>
      </div>

      {/* Article display toggles */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6 space-y-4" data-testid="article-display-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-white">Prikaz članka</h2>
        <label className="flex items-start gap-3 p-3 rounded-md bg-zinc-950/60 border border-zinc-800 cursor-pointer">
          <input
            type="checkbox"
            checked={!!ad.show_inline_source_credit}
            onChange={e => updateAD('show_inline_source_credit', e.target.checked)}
            className="mt-0.5 accent-red-600"
            data-testid="toggle-inline-source-credit"
          />
          <span>
            <span className="block text-sm font-semibold text-zinc-100">Prikaži inline "Izvor i fotografija: …" u tekstu članka</span>
            <span className="block text-[11px] text-zinc-400 mt-0.5">
              Kada je isključeno, sustav automatski uklanja retke koji počinju s <code>Izvor:</code>, <code>Izvor i fotografija:</code>, <code>Fotografija:</code> ili <code>Foto:</code> iz tijela članka prije prikaza na javnom portalu. Zasebna kartica "Izvor" ispod članka i dalje se prikazuje.
            </span>
          </span>
        </label>
      </div>

      {/* Preloader animation picker */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6 space-y-4" data-testid="preloader-settings-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-white">Preloader animacija (E-Mina mina)</h2>
        <p className="text-[11px] text-zinc-400">Odaberite animaciju koja se prikazuje dok se javni portal učitava. Klik na karticu odabire animaciju; zatim spremite postavke.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: 'bounce', label: 'Skok (bounce)', desc: 'Logo skače gore-dolje sa sjenom.' },
            { k: 'pulse',  label: 'Puls / mina',    desc: 'Logo pulsira s crvenim prstenovima.' },
            { k: 'wobble', label: 'Njihanje + iskre', desc: 'Logo se ljulja, iskre frcaju oko njega.' },
            { k: 'mina',   label: 'Mina — eksplozija', desc: 'Puni se i eksplodira otvarajući stranicu. ZADANA.' },
          ].map(opt => (
            <button key={opt.k} type="button" onClick={() => updatePre('animation', opt.k)}
                    className={`text-left rounded-xl border p-3 transition-colors ${pre.animation === opt.k ? 'bg-red-600/10 border-red-500' : 'bg-zinc-950 border-zinc-800 hover:border-zinc-600'}`}
                    data-testid={`preloader-opt-${opt.k}`}>
              <div className="h-32 flex items-center justify-center rounded-lg bg-slate-950/70 mb-3 overflow-hidden">
                <BouncingLogoLoader variant={opt.k} size={64} />
              </div>
              <p className="text-xs font-bold text-white">{opt.label}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">{opt.desc}</p>
              {pre.animation === opt.k && <p className="text-[10px] text-red-400 mt-1 font-bold">● AKTIVNO</p>}
            </button>
          ))}
        </div>
      </div>
      {/* Personalised "Za tebe" widget toggle */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6 space-y-4" data-testid="personalized-feed-section">
        <h2 className="text-sm font-bold uppercase tracking-wider text-white">Personalizirani feed "Za tebe"</h2>
        <p className="text-[11px] text-zinc-400">Widget na naslovnici koji prijavljenim korisnicima prikazuje članke usklađene s interesima koje su odabrali pri registraciji.</p>
        <label className="flex items-start gap-3 p-3 rounded-md bg-zinc-950/60 border border-zinc-800 cursor-pointer">
          <input
            type="checkbox"
            checked={pf.enabled !== false}
            onChange={e => updatePF('enabled', e.target.checked)}
            className="mt-0.5 accent-red-600"
            data-testid="toggle-personalized-feed"
          />
          <span>
            <span className="block text-sm font-semibold text-zinc-100">Prikaži "Za tebe" widget na naslovnici</span>
            <span className="block text-[11px] text-zinc-400 mt-0.5">
              Kada je uključeno, prijavljeni korisnici s odabranim interesima vide personaliziranu mrežu članaka odmah ispod hero sekcije. Anonimni posjetitelji ga ne vide.
            </span>
          </span>
        </label>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Naslov widgeta (HR)</label>
            <input value={pf.title_hr || 'Za tebe'} onChange={e => updatePF('title_hr', e.target.value)}
                   className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-white text-sm" data-testid="pf-title-input" />
          </div>
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Broj članaka (1–12)</label>
            <input type="number" min={1} max={12} value={pf.limit ?? 6} onChange={e => updatePF('limit', Math.min(12, Math.max(1, parseInt(e.target.value || '6', 10))))}
                   className="w-full px-3 py-2 bg-zinc-950 border border-zinc-700 rounded text-white text-sm" data-testid="pf-limit-input" />
          </div>
        </div>
      </div>
    </div>
  );
}
